import { z } from "zod";
import { OrderStripeRefundSchema, orderStripeRefund } from "../orders/stripe";
export const StripeEventSchema = z.object({
    id: z.string().min(1),
    account: z.string().min(1),
    type: z.enum([
        "checkout.session.completed",
        "checkout.session.async_payment_succeeded",
        "checkout.session.async_payment_failed",
        "checkout.session.expired",
    ]),
    data: z.object({
        object: z.object({
            id: z.string().min(1),
            metadata: z.object({ checkout_id: z.string().optional() }).optional(),
        }),
    }),
});
/** The only application-fee seam. Direct charges have no platform fee in v1. */
export function applicationFee(_snapshot) {
    return null;
}
const IntentSchema = z.object({
    id: z.string(),
    status: z.string(),
    latest_charge: z
        .union([
        z.string(),
        z.object({
            balance_transaction: z
                .union([
                z.string(),
                z.object({ fee: z.number().int(), net: z.number().int(), currency: z.string() }),
            ])
                .nullable(),
        }),
    ])
        .nullable()
        .optional(),
});
const SessionSchema = z.object({
    id: z.string(),
    url: z.string().nullable(),
    status: z.enum(["open", "complete", "expired"]),
    payment_status: z.enum(["paid", "unpaid", "no_payment_required"]),
    amount_total: z.number().int().nonnegative(),
    currency: z.string(),
    expires_at: z.number().int(),
    metadata: z.object({ checkout_id: z.string() }),
    payment_intent: z.union([z.string(), IntentSchema]).nullable(),
});
function sessionFrom(value) {
    const s = SessionSchema.parse(value);
    const intent = typeof s.payment_intent === "object" ? s.payment_intent : null;
    const charge = intent?.latest_charge;
    const balance = typeof charge === "object" ? charge?.balance_transaction : null;
    return {
        id: s.id,
        url: s.url,
        status: s.status,
        payment: s.payment_status,
        processing: intent?.status === "processing",
        checkoutId: s.metadata.checkout_id,
        amount: s.amount_total,
        currency: s.currency.toUpperCase(),
        paymentIntent: typeof s.payment_intent === "string"
            ? s.payment_intent
            : (intent?.id ?? null),
        fee: typeof balance === "object" ? (balance?.fee ?? null) : null,
        net: typeof balance === "object" ? (balance?.net ?? null) : null,
        balanceCurrency: typeof balance === "object" ? (balance?.currency.toUpperCase() ?? null) : null,
        expiresAt: s.expires_at,
    };
}
const AccountSchema = z.object({
    id: z.string(),
    type: z.literal("standard"),
    country: z.string(),
    charges_enabled: z.boolean(),
    details_submitted: z.boolean(),
});
function accountFrom(value) {
    const a = AccountSchema.parse(value);
    return {
        id: a.id,
        country: a.country,
        ready: a.charges_enabled && a.details_submitted,
    };
}
/**
 * Every shop request names the API version it was written against, so a change to an account's default version in the
 * Stripe dashboard can't change response shapes under us. Webhooks follow the account's default version, but the
 * handlers read only stable fields (type, account, object id, metadata, payment_intent) and fetch the rest through this
 * client. Change it deliberately, with the shop's Stripe tests.
 */
export const STRIPE_API_VERSION = "2025-08-27.basil";
export class StripeRequestError extends Error {
    status;
    type;
    constructor(status, type) {
        super(`Stripe request failed (${status})`);
        this.status = status;
        this.type = type;
    }
    get definitive() {
        // Conflicts and rate limits are retryable; other client errors did not create a session.
        return this.type !== "idempotency_error" && this.status >= 400 && this.status < 500 && ![409, 429].includes(this.status);
    }
}
/** Only server-side callers have this client; every payment/refund request acts on the connected account. */
export class StripeRestClient {
    secret;
    fetcher;
    now;
    options;
    constructor(secret, fetcher = fetch, now = Date.now, options = {}) {
        this.secret = secret;
        this.fetcher = fetcher;
        this.now = now;
        this.options = options;
    }
    async call(path, fields, account, key) {
        const headers = {
            Authorization: `Bearer ${this.secret}`,
            "Stripe-Version": STRIPE_API_VERSION,
        };
        if (account)
            headers["Stripe-Account"] = account;
        if (key)
            headers["Idempotency-Key"] = key;
        if (fields)
            headers["Content-Type"] = "application/x-www-form-urlencoded";
        const response = await this.fetcher(`https://api.stripe.com/v1/${path}`, {
            method: fields ? "POST" : "GET",
            headers,
            ...(fields ? { body: new URLSearchParams(fields) } : {}),
            signal: AbortSignal.timeout(15000),
        });
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            // Retain only Stripe's error category, never its payload or customer details.
            throw new StripeRequestError(response.status, typeof error?.error?.type === "string" ? error.error.type : undefined);
        }
        return response.json();
    }
    async createAccount({ email, country, siteId, }) {
        return accountFrom(await this.call("accounts", { type: "standard", email, country, "metadata[shop_site_id]": siteId }, undefined, `shop-account-${siteId}`));
    }
    async account(id) {
        return accountFrom(await this.call(`accounts/${encodeURIComponent(id)}`));
    }
    async onboarding(id, refreshUrl, returnUrl) {
        return z
            .object({ url: z.string().url() })
            .parse(await this.call("account_links", {
            account: id,
            type: "account_onboarding",
            refresh_url: refreshUrl,
            return_url: returnUrl,
        })).url;
    }
    async createSession(account, checkoutId, snapshot, successUrl, cancelUrl, expiresAt) {
        const fields = {
            mode: "payment",
            success_url: successUrl,
            cancel_url: cancelUrl,
            customer_email: snapshot.contact.email,
            "metadata[checkout_id]": checkoutId,
            "payment_intent_data[metadata][checkout_id]": checkoutId,
        };
        snapshot.items.forEach((item, i) => {
            fields[`line_items[${i}][quantity]`] = String(item.quantity);
            fields[`line_items[${i}][price_data][currency]`] =
                snapshot.currency.toLowerCase();
            fields[`line_items[${i}][price_data][unit_amount]`] = String(item.unitPrice);
            fields[`line_items[${i}][price_data][product_data][name]`] =
                `${item.name}${item.variant ? ` · ${item.variant}` : ""}`;
        });
        // Dynamic payment methods include the account's enabled eligible card/wallet/local/delayed methods. Stripe never collects delivery addresses.
        if (snapshot.shipping) {
            fields["shipping_options[0][shipping_rate_data][type]"] = "fixed_amount";
            fields["shipping_options[0][shipping_rate_data][fixed_amount][amount]"] =
                String(snapshot.shipping);
            fields["shipping_options[0][shipping_rate_data][fixed_amount][currency]"] = snapshot.currency.toLowerCase();
            fields["shipping_options[0][shipping_rate_data][display_name]"] =
                snapshot.delivery.name ?? "Delivery";
        }
        const fee = applicationFee(snapshot);
        if (fee !== null)
            fields["payment_intent_data[application_fee_amount]"] = String(fee);
        // Fresh attempts get latency headroom. A known accepted expiry is used only
        // for an idempotent replay, never for an attempt that failed before creation.
        fields.expires_at = String(expiresAt ?? Math.ceil(this.now() / 1000) + 32 * 60);
        try {
            return sessionFrom(await this.call("checkout/sessions", fields, account, `shop-checkout-${checkoutId}`));
        }
        catch (error) {
            if (!(error instanceof StripeRequestError) || error.type !== "idempotency_error")
                throw error;
            // A lost response can hide an accepted session. Changing its expiry would
            // fail Stripe's parameter comparison; recover it by metadata without
            // rotating the creation key or creating a second session.
            let cursor = "";
            for (let page = 0; page < 5; page++) {
                const query = new URLSearchParams({ limit: "100", "created[gte]": String(Math.floor(this.now() / 1000) - 86400) });
                if (cursor)
                    query.set("starting_after", cursor);
                const result = z.object({ has_more: z.boolean(), data: z.array(z.object({ id: z.string(), metadata: z.object({ checkout_id: z.string().optional() }) })) })
                    .parse(await this.call(`checkout/sessions?${query}`, undefined, account));
                const existing = result.data.find((session) => session.metadata.checkout_id === checkoutId);
                if (existing)
                    return this.session(account, existing.id);
                if (!result.has_more || !result.data.length)
                    break;
                cursor = result.data.at(-1).id;
            }
            throw error; // Ambiguous work stays reserved for reconciliation/attention.
        }
    }
    async session(account, id) {
        const expand = this.options.includeFees === false ? "payment_intent" : "payment_intent.latest_charge.balance_transaction";
        return sessionFrom(await this.call(`checkout/sessions/${encodeURIComponent(id)}?expand[]=${expand}`, undefined, account));
    }
    async expire(account, id) {
        try {
            await this.call(`checkout/sessions/${encodeURIComponent(id)}/expire`, {}, account);
        }
        catch {
            const current = await this.session(account, id);
            if (current.status === "open")
                throw new Error("Stripe session could not be expired");
            return current;
        }
        return this.session(account, id);
    }
    async orderRefunds(account, intent) {
        const refunds = [];
        let cursor = "";
        for (let page = 0; page < 5; page++) {
            const query = new URLSearchParams({ payment_intent: intent, limit: "100" });
            if (cursor)
                query.set("starting_after", cursor);
            const result = z.object({ has_more: z.boolean(), data: z.array(OrderStripeRefundSchema) }).parse(await this.call(`refunds?${query}`, undefined, account));
            refunds.push(...result.data.map(orderStripeRefund));
            if (!result.has_more)
                return refunds;
            cursor = result.data.at(-1)?.id ?? "";
            if (!cursor)
                throw new Error("Refund history is incomplete");
        }
        throw new Error("Refund history requires attention");
    }
    async orderRefund(account, intent, id, amount) {
        // Recover accepted work before POST, including after Stripe's 24-hour key window.
        const prior = (await this.orderRefunds(account, intent)).find(r => r.operationId === id);
        if (prior)
            return prior;
        return orderStripeRefund(await this.call("refunds", { payment_intent: intent, amount: String(amount), "metadata[order_refund_id]": id }, account, `shop-order-refund-${id}`));
    }
    async orderRefundDetails(account, id) {
        return orderStripeRefund(await this.call(`refunds/${encodeURIComponent(id)}`, undefined, account));
    }
    async refund(account, paymentIntent, checkoutId) {
        // Recover our accepted operation even after Stripe's idempotency window expires.
        const intent = z.object({ amount_received: z.number().int().positive() }).parse(await this.call(`payment_intents/${encodeURIComponent(paymentIntent)}`, undefined, account));
        const refunds = await this.orderRefunds(account, paymentIntent);
        const existing = refunds.find(r => (r.metadata?.checkout_id === checkoutId && r.metadata?.stackje_kind === "sold_out") || (r.amount === intent.amount_received && (r.metadata?.checkout_id === checkoutId || r.status === "succeeded")));
        if (existing)
            return existing;
        return orderStripeRefund(await this.call("refunds", {
            payment_intent: paymentIntent, "metadata[checkout_id]": checkoutId, "metadata[stackje_kind]": "sold_out",
        }, account, `shop-sold-out-${checkoutId}`));
    }
}
/** Stripe's timestamped HMAC, with a five-minute replay tolerance and constant-time comparison. */
export async function verifyConnectSignature(body, header, secret, now = Date.now()) {
    if (!header || !secret)
        return false;
    const pairs = header.split(",").map((v) => v.split("="));
    const timestamp = pairs.find((p) => p[0] === "t")?.[1];
    if (!timestamp ||
        !/^\d+$/.test(timestamp) ||
        Math.abs(now / 1000 - Number(timestamp)) > 300)
        return false;
    const key = await crypto.subtle.importKey("raw", new TextEncoder().encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
    const signed = new Uint8Array(await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(`${timestamp}.${body}`)));
    const hex = [...signed].map((v) => v.toString(16).padStart(2, "0")).join("");
    return pairs.some(([type, value]) => {
        if (type !== "v1" || !value || value.length !== hex.length)
            return false;
        let diff = 0;
        for (let i = 0; i < hex.length; i++)
            diff |= hex.charCodeAt(i) ^ value.charCodeAt(i);
        return diff === 0;
    });
}
